/**
 * @constructor
 */
class maxkir_ListItemSearcher {
    private _callback: () => void;
    private _section_limit: number;

    private _lists_loaded: boolean;
    private _tasks_loaded: boolean;

    private _text: string;
    private _scope_list_id: number;
    private _scope_task_id: number;

    private _loading_for_key: string|null;

    constructor(section_limit?: number) {
        this._callback = () => {};
        this._text = '';
        this._section_limit = section_limit || 15;

        this._loading_for_key = null;
    }

    /**
     * @param text text to filter
     * @param {number} [max_items_per_section]
     */
    setSearchedText(text, max_items_per_section) {

        var max = max_items_per_section || this._section_limit;
        if (text !== this._text || max !== this._section_limit) {
            this._text = text;
            this._section_limit = max;

            this._tasks_loaded = false;
            maxkir.info("setSearchedText: '" + text + "'");
        }
    }

    getParentContext() {
        return {
            list_id: this._scope_list_id,
            task_id: this._scope_task_id
        }
    }

    setParentContext(list_id: number, task_id: number) {
        if (this._scope_list_id !== list_id || this._scope_task_id !== task_id) {
            this._tasks_loaded = false;
            this._scope_list_id = list_id;
            this._scope_task_id = task_id;
            maxkir.info("setParentContext: list:" + list_id + "' task:" + task_id);
        }
    }

    /**
     * Set max number of items to be returned in each section of results
     * @param {number} limit
     */
    setSectionLimit(limit) {
        this._section_limit = limit;
    }

    setHasMoreDataCallback(callback) {
        this._callback = callback ? callback : () => {};
    }

    isNothingFound() {
        return this.hasSomeData() &&
            !this.getFoundLists().length &&
            !this.getFoundListItems().length;
    }

    hasSomeData() {
        return this._all_lists().length;
    }

    /**
     * Return list of lists relevant to the search
     * @return Array<maxkir.List>
     */
    getFoundLists() {

        if (this._scope_list_id || this._scope_task_id) {
            return [];
        }

        if (!this._lists_loaded) {
            var that = this;
            that._lists_loaded = true;
            let loading = true;
            const myKey = that._start_loading_with_key("$lists$");
            maxkir.Lists.process_list_data((resultArr) => {
                if (loading) {
                    // callback can be called several times, so we should keep _loading_count valid
                    loading = false;
                    that._stop_loading_with_key(myKey);
                }
                that._update_on_more_data();
            });
        }

        var result = [];
        let [tag_filter, terms] = this._prepare_terms()

        for (var list_data of this._all_lists()) {
            if (result.length >= this._section_limit) break;
            if (this._matches(list_data.name, list_data, terms, tag_filter)) {
                result.push(list_data);
            }
        }
        result = this._sort_list_items(result);

        return result;
    }

    _update_on_more_data() {
        setTimeout(() => {
            this._callback();
        }, 10);
    }

    _all_lists() {
        return maxkir.Lists.get_list_data(false);
    }

    /**
     *
     * @param {string} s text to look for match in
     * @param {object} item_data - should have .tags property to be matched by tags
     * @param {String[]} terms - array of text terms to be matched
     * @param {maxkir.FindFilterProvider.TagFilter} tag_filter
     * @return {boolean}
     * @private
     */
    _matches(s, item_data, terms, tag_filter) {
        if (this._scope_task_id === 0 && item_data.parent_id > 0) {
            return false;
        }
        if (this._scope_task_id && this._scope_task_id !== item_data.parent_id) {
            return false;
        }
        if (this._scope_list_id && this._scope_list_id !== item_data.checklist_id) {
            return false;
        }

        // Check tag match, if needed
        if (tag_filter.get_active()) {
            var tag_suffix = maxkir.Task.smart_attributes(item_data);
            s = s + " #" + tag_suffix;
        }

        s = s.toLowerCase();
        terms = terms.map((t) => t.toLowerCase());

        var penalty_text_match = this._matches_text_terms(s, terms)
        if (penalty_text_match) {
            item_data._penalty_match = penalty_text_match;
        }

        return penalty_text_match && this._matches_tag_terms(s, terms);
    }

    _matches_text_terms(s, terms) {
        var words = [];
        for (var i = 0; i < terms.length; i++) {
            var term = terms[i];
            if (!term.length || term.charAt(0) === '#') continue;
            words.push(term)
        }

        var that = this;
        var results = {};
        var foundOrder = [];
        for (i = 0; i < words.length; i++) {
            var word = words[i];

            var matchIdx = this._find_idx(s, word, function (foundIdx) {
                return !that.isAlNum(s.charAt(foundIdx - 1)) && !results[foundIdx]
            });

            if (matchIdx < 0) {
                return false;
            }
            results[matchIdx] = word;
            foundOrder.push(matchIdx);
        }

        // calculate match order penalty
        var penalty = 1;
        for (var i = 1; i < foundOrder.length; i++) {
            if (foundOrder[i] < foundOrder[i - 1]) {
                penalty++;
            }
        }
        // the higher the value, the worse
        return penalty;
    }

    _find_idx(s, word, accepter_function) {
        var idx = s.indexOf(word, 0);
        while (idx >= 0) {
            if (accepter_function(idx)) {
                break;
            } else {
                idx = s.indexOf(word, idx + 1);
            }
        }
        return idx;
    }

    _matches_tag_terms(s, terms) {
        for (var i = 0; i < terms.length; i++) {
            var term = terms[i];
            if (!term.length || term.charAt(0) !== '#') {
                continue;
            }
            if (s.indexOf(term) < 0) return false;
        }
        return true;
    }

    isAlNum(char) {
        if (char >= '0' && char <= '9') return true;
        return char.toLowerCase() !== char.toUpperCase();
    }

    isLoading() {
        return this._loading_for_key !== null;
    }

    isLoadingItem(list_id: number, task_id: number) {
        return this._loading_for_key === "$$$_" + list_id + "_" + task_id;
    }

    getFoundListItems() {
        var result = [];

        if (this._text.length === 0 && !this._scope_list_id && !this._scope_task_id) {
            // Do not include list items when there is no filtering
            return result;
        }

        let [tag_filter, terms] = this._prepare_terms()

        for (var item_data of this._all_task_data()) {
            if (this._matches(item_data.content, item_data, terms, tag_filter)) {
                result.push(item_data);
            }
        }

        result = this._sort_list_items(result);
        result = result.slice(0, this._section_limit)

        if (result.length < this._section_limit && !this._tasks_loaded) {
            var that = this;
            this._tasks_loaded = true;

            const promise = this._scope_list_id ?
                this._search_tasks_on_server_for_level() :
                this._search_tasks_on_server();

            promise.then((responseJson: any) => {
                var tasks = responseJson.tasks;
                if (responseJson.lists) {
                    maxkir.Lists.add_lists_data(responseJson.lists, false)
                }
                maxkir.Task.add_fetched_tasks(tasks);
                that._update_on_more_data();
            }, (err) => {
                maxkir.warn("Could not search for list items", err)
            })
        }

        return result;
    }

    _prepare_terms() {
        var tag_filter = maxkir.FindFilterProvider.TagFilter
        tag_filter.parse_line(this._text)
        var terms = this._text.split(/\s+/);
        return [tag_filter, terms];
    }

    /**
     *
     * @param {maxkir.TaskData[]} items
     * @private
     */
    _sort_list_items(items) {

        for (var t of items) {
            if (t.updated_at) {
                // List or list item
                t._updated_at_i = Date.parse(t.updated_at);
            }
            if (t.checklist_id) {
                // List item
                var l = maxkir.Lists.data(t.checklist_id)
                if (l && l.user_updated_at) {
                    t._list_updated_at = Date.parse(l.user_updated_at);
                }
            } else if (t.user_updated_at) {
                t._list_updated_at = Date.parse(t.user_updated_at);
            }
        }

        const compare_by_list_updated_at = function(a, b) {
            if (a._list_updated_at && !b._list_updated_at) return -1;
            if (b._list_updated_at && !a._list_updated_at) return 1;

            if (a._list_updated_at !== b._list_updated_at) {
                return b._list_updated_at > a._list_updated_at ? 1 : -1;
            }
            return 0;
        }

        const one_level = this._scope_task_id === 0 || this._scope_task_id > 0;
        return items.sort((a, b) => {
            // debugger
            if (a._penalty_match !== b._penalty_match) {
                return a._penalty_match > b._penalty_match ? 1 : -1;
            }

            if (a.checklist_id !== b.checklist_id) {
                // Only for list items
                if (a.checklist_id === maxkir.checklist_id) return -1;
                if (b.checklist_id === maxkir.checklist_id) return 1;

                const res = compare_by_list_updated_at(a, b);
                if (res !== 0) return res;
            }

            if (one_level && a.position !== b.position) {
                return a.position - b.position;
            }

            if (!a.checklist_id && !b.checklist_id) {
                // comparing lists
                const res = compare_by_list_updated_at(a, b);
                if (res !== 0) return res;
            }

            if (a._updated_at_i && !b._updated_at_i) {
                return -1;
            }
            if (b._updated_at_i && !a._updated_at_i) {
                return 1;
            }
            if (b._updated_at_i !== a._updated_at_i) {
                return b._updated_at_i > a._updated_at_i ? 1 : -1;
            }
            return b.id - a.id;
        });
    }

    /**
     * @return Promise<any>
     * @private
     */
    _search_tasks_on_server() {
        if (!this._text) {
            return Promise.resolve({});
        }

        var that = this;
        const myKey = this._start_loading_with_key(that._text);
        return new Promise(function (resolve, reject) {
            maxkir.AjaxRequest("/search.json", {
                method: "get",
                parameters: "ver=2&what=" + encodeURIComponent(that._text),
                onComplete(response) {
                    that._stop_loading_with_key(myKey);

                    if (response.request.success() && response.responseJSON) {
                        resolve(response.responseJSON);
                    }
                    reject(response);
                }
            })
        })
    }

    _start_loading_with_key(key) {
        this._loading_for_key = "$$$_" + key;
        maxkir.info("Start call server for " + this._loading_for_key);
        return this._loading_for_key;
    }

    _stop_loading_with_key(key) {
        if (key === this._loading_for_key) {
            maxkir.info("Stop  call server for " + key);
            this._loading_for_key = null;
        }
    }

    /**
     * @return Promise<any>
     * @private
     */
    _search_tasks_on_server_for_level() {
        var that = this;
        const myKey = this._start_loading_with_key("" + that._scope_list_id + "_" +(that._scope_task_id || 0));
        return new Promise(function (resolve, reject) {
            maxkir.AjaxRequest("/checklists/" + that._scope_list_id + "/tasks.json?parent_id=" + (that._scope_task_id || 0), {
                method: "get",
                onComplete(response) {
                    that._stop_loading_with_key(myKey);

                    if (response.request.success() && response.responseJSON) {
                        resolve({tasks: response.responseJSON});
                    }
                    reject(response);
                }
            })
        })
    }

    _all_task_data() {
        return Object.values(maxkir.TaskData).concat(Object.values(maxkir.TaskDataFetched));
    }

    /**
     * Called when searcher should stop working and should clear all listeners
     */
    stop() {
        this._callback = () => {};
    }
}


maxkir.ListItemSearcher = maxkir_ListItemSearcher;
